// SectionTabs — a reusable horizontal tab strip that consolidates a group of
// related routes into ONE sidebar entry (the pattern first used for Assurance).
// Each tab is still its own route (deep-links + back button work natively); this
// wrapper renders the strip above the page and navigates on tab change. Page
// bodies are untouched — each keeps its own PageHeader below the strip.
//
// The concrete section wrappers (AssuranceTabs, InventoryTabs, …) are thin
// configs of TAB_DEFS built on this component.
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Clapperboard, HeartPulse, FileVideo, Image as ImageIcon, Database, PlayCircle,
  Disc, Cpu, Boxes, MapPin, Box, Network, HardDriveDownload, BarChart3, FileBarChart,
  Ticket, Gauge, ArrowUpCircle, Building2, FileText, Server, Zap, Users,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Tabs, type TabDef } from '@/components/ui/Tabs';

export interface SectionTabDef { value: string; label: string; icon: TabDef['icon'] }

/** Generic section tab strip. `tabs[].label` is the bare `items.*` i18n key. */
export function SectionTabs({ tabs, ariaLabel, fallback, children }: {
  tabs: SectionTabDef[];
  ariaLabel: string;
  /** Route to treat as active when nothing matches (usually the landing tab). */
  fallback: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('nav');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Active = the most specific (longest) route that matches the current path.
  const active =
    [...tabs]
      .sort((a, b) => b.value.length - a.value.length)
      .find((tab) => pathname === tab.value || pathname.startsWith(tab.value + '/'))?.value
    ?? fallback;

  const uiTabs: TabDef[] = tabs.map((tab) => ({
    value: tab.value,
    label: t(`items.${tab.label}`, tab.label),
    icon: tab.icon,
  }));

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        <Tabs tabs={uiTabs} value={active} onChange={(v) => navigate(v)} aria-label={ariaLabel} />
      </div>
      {children}
    </div>
  );
}

// ── Section configs (one tabbed sidebar entry each) ─────────────────────────

const ASSURANCE_TABS: SectionTabDef[] = [
  { value: '/video-assurance', label: 'Overview', icon: Clapperboard },
  { value: '/video-assurance/cameras', label: 'Camera Health', icon: HeartPulse },
  { value: '/video-assurance/recording-verification', label: 'Recording Verification', icon: FileVideo },
  { value: '/video-assurance/blur', label: 'Image Quality', icon: ImageIcon },
  { value: '/video-assurance/retention', label: 'Retention Compliance', icon: Database },
  { value: '/video-assurance/playback', label: 'Playback Verification', icon: PlayCircle },
  { value: '/recording', label: 'Recording Assurance', icon: Disc },
  { value: '/ai-health', label: 'AI Analytics Health', icon: Cpu },
];

const INVENTORY_TABS: SectionTabDef[] = [
  { value: '/devices', label: 'Devices', icon: Boxes },
  { value: '/sites', label: 'Sites', icon: MapPin },
  { value: '/assets', label: 'Asset Inventory', icon: Box },
  { value: '/topology', label: 'Topology', icon: Network },
  { value: '/firmware', label: 'Firmware Compliance', icon: HardDriveDownload },
];

const INSIGHTS_TABS: SectionTabDef[] = [
  { value: '/analytics', label: 'Health Analytics', icon: BarChart3 },
  { value: '/reports', label: 'Reports Center', icon: FileBarChart },
];

const SERVICE_DESK_TABS: SectionTabDef[] = [
  { value: '/tickets', label: 'Tickets', icon: Ticket },
  { value: '/sla', label: 'SLA Monitoring', icon: Gauge },
  { value: '/escalations', label: 'Escalations', icon: ArrowUpCircle },
  { value: '/vendors', label: 'Vendors', icon: Building2 },
  { value: '/vendor-performance', label: 'Vendor Performance', icon: BarChart3 },
  { value: '/contracts', label: 'Contracts', icon: FileText },
  { value: '/contract-dashboard', label: 'Dashboard', icon: Gauge },
];

const CONFIGURATION_TABS: SectionTabDef[] = [
  { value: '/edges', label: 'Edge Nodes', icon: Server },
  { value: '/automation', label: 'Rules & Automation', icon: Zap },
  { value: '/team', label: 'Users & Team', icon: Users },
  { value: '/settings', label: 'Settings', icon: SettingsIcon },
];

export const AssuranceTabs = ({ children }: { children: ReactNode }) => (
  <SectionTabs tabs={ASSURANCE_TABS} ariaLabel="Assurance sections" fallback="/video-assurance">{children}</SectionTabs>
);
export const InventoryTabs = ({ children }: { children: ReactNode }) => (
  <SectionTabs tabs={INVENTORY_TABS} ariaLabel="Inventory sections" fallback="/devices">{children}</SectionTabs>
);
export const InsightsTabs = ({ children }: { children: ReactNode }) => (
  <SectionTabs tabs={INSIGHTS_TABS} ariaLabel="Insights sections" fallback="/analytics">{children}</SectionTabs>
);
export const ServiceDeskTabs = ({ children }: { children: ReactNode }) => (
  <SectionTabs tabs={SERVICE_DESK_TABS} ariaLabel="Service desk sections" fallback="/tickets">{children}</SectionTabs>
);
export const ConfigurationTabs = ({ children }: { children: ReactNode }) => (
  <SectionTabs tabs={CONFIGURATION_TABS} ariaLabel="Configuration sections" fallback="/edges">{children}</SectionTabs>
);
